import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticlesGrid from './ArticlesGrid.jsx';
import ArticleEditor from './ArticleEditor.jsx';
import EtiquetaEditor from './EtiquetaEditor.jsx';
import { saveArticle, uploadMedia, fetchArticleById, selectCurrentArticle, selectArticleStatus, clearCurrentArticle } from './ArticleSlice';
import { fetchArticleTypes, selectArticleTypeDict } from './ArticleTypeSlice';

// The section is one of two views, not a pair of tabs: the list is home, and
// opening an item pushes a full-screen editor with its own back button. Tabs
// implied you could sit in "Editor" with nothing loaded, which is what made the
// old flow confusing — and a tab strip is wasted vertical space on a phone.
const VIEW = {
    LIST: 'list',
    EDITOR: 'editor',
};

// #region ARTICLES
const Articles = () => {
    const dispatch = useDispatch();

    const [view, setView] = useState(VIEW.LIST);
    const [currentDraft, setCurrentDraft] = useState(null); // { title, content }
    const [savedSnapshot, setSavedSnapshot] = useState(null); // last saved (draft or final)
    const [isLoadingArticle, setIsLoadingArticle] = useState(false); // flag to track if we're loading an article

    // Redux selectors for article editing
    const currentArticle = useSelector(selectCurrentArticle);
    const articleStatus = useSelector(selectArticleStatus);
    const articleTypeDict = useSelector(selectArticleTypeDict);

    // Which editor to show when creating something new. Editing an existing
    // item ignores this — the item's own type decides (see `editorForm` below).
    const [newItemTypeId, setNewItemTypeId] = useState(null);

    // Fetch article types once on mount
    useEffect(() => {
        dispatch(fetchArticleTypes());
    }, [dispatch]);

    // Normalize currentArticle shape (id, body/content) for the editor
    const normalizedCurrentArticle = useMemo(() => {
        if (!currentArticle) return null;
        const id = currentArticle.id || currentArticle.idArticle || currentArticle.id_article;
        let content = currentArticle.content || currentArticle.body;
        return { ...currentArticle, id, content: content || null };
    }, [currentArticle]);

    // The article type in play: the loaded item's when editing, otherwise the
    // one picked for a new item. Its `form` selects the editor — 'article'
    // (Draft.js rich text, the default for every existing type) or 'etiqueta'
    // (a fielded record form).
    const activeTypeId = (normalizedCurrentArticle && normalizedCurrentArticle.idArticleType) || newItemTypeId;
    const activeType = articleTypeDict[activeTypeId];
    const editorForm = (activeType && activeType.form) || 'article';
    const isNewItem = !normalizedCurrentArticle;

    // Determine if there are unsaved changes
    const dirty = useMemo(() => {
        if (!currentDraft) {
            return false;
        }

        if (!savedSnapshot) {
            // nothing saved yet; treat empty editor as clean
            const emptyContent = !currentDraft.title && currentDraft.content?.blocks?.every((b) => b.text === '');
            return !emptyContent;
        }

        // Check title changes
        const titleChanged = (savedSnapshot.title || '') !== (currentDraft.title || '');

        // Normalize both contents for comparison
        let savedContent = savedSnapshot.content;
        if (typeof savedContent === 'string') {
            try {
                savedContent = JSON.parse(savedContent);
            } catch (e) {
                // If parsing fails, keep as is
            }
        }

        let draftContent = currentDraft.content;
        if (typeof draftContent === 'string') {
            try {
                draftContent = JSON.parse(draftContent);
            } catch (e) {
                // If parsing fails, keep as is
            }
        }

        // Deep comparison of content
        const contentChanged = JSON.stringify(savedContent) !== JSON.stringify(draftContent);

        return titleChanged || contentChanged;
    }, [currentDraft, savedSnapshot]);

    // When loading an existing article, capture the first NON-EMPTY content from editor as baseline
    useEffect(() => {
        if (isLoadingArticle && currentDraft && !savedSnapshot) {
            // Check if content has actually loaded (not just an empty initial state)
            const hasActualContent = currentDraft.title || (currentDraft.content?.blocks && currentDraft.content.blocks.some((b) => b.text));

            if (hasActualContent) {
                // Content has loaded - use this as baseline
                setSavedSnapshot({
                    title: currentDraft.title,
                    content: currentDraft.content,
                });
                setIsLoadingArticle(false);
            }
        }
    }, [isLoadingArticle, currentDraft, savedSnapshot]);

    async function saveHandler(data) {
        try {
            const resultAction = await dispatch(saveArticle(data));
            if (saveArticle.fulfilled.match(resultAction)) {
                // Record what was actually written as the new clean baseline.
                // Clearing it instead (the old behaviour) made `dirty` true again
                // the instant a save finished, so leaving the editor wrote a
                // second, identical draft version — and a draft is forced
                // invisible, which would undo a publish that had just succeeded.
                setSavedSnapshot({ title: data.title, content: data.content });
                return resultAction.payload;
            } else {
                console.error('Failed to save article:', resultAction.error);
                return false;
            }
        } catch (error) {
            console.error('Failed to save article:', error);
            return false;
        }
    }

    // Close the editor and forget its state. No saving of any kind — this is
    // what the editor calls *after* a successful save, where writing another
    // (draft) version would both bump the version count and, because a draft is
    // forced invisible server-side, immediately unpublish what was just published.
    const closeEditor = useCallback(() => {
        dispatch(clearCurrentArticle());
        setCurrentDraft(null);
        setSavedSnapshot(null);
        setNewItemTypeId(null);
        setView(VIEW.LIST);
    }, [dispatch]);

    // Leaving the editor by the back button keeps whatever was typed: anything
    // unsaved is written as a draft first, exactly as the old tab switch did.
    const handleBackToList = useCallback(async () => {
        if (dirty && currentDraft) {
            try {
                currentDraft.isDraft = true; // mark as draft
                await saveHandler(currentDraft);
            } catch (e) {
                console.error('Auto draft save failed before leaving editor:', e);
            }
        }

        closeEditor();
        // saveHandler is recreated on every render but never changes behaviour;
        // listing it would re-run this callback on each keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty, currentDraft, closeEditor]);

    // Handle article selection from the list
    const handleEditArticle = async (article) => {
        // Get the article ID from different possible field names
        const articleId = article.id || article.idArticle || article.id_article;

        if (!articleId) {
            console.error('No article ID found in:', article);
            return;
        }

        try {
            // Fetch the full article data including content
            const resultAction = await dispatch(fetchArticleById(articleId));

            if (fetchArticleById.fulfilled.match(resultAction)) {
                // Clear any previous state and mark that we're loading an article
                setCurrentDraft(null);
                setSavedSnapshot(null);
                setIsLoadingArticle(true);
                setNewItemTypeId(null);

                // The editor will normalize and send content via onContentChange
                // Our useEffect will capture that first update as the baseline
                setView(VIEW.EDITOR);
            } else {
                console.error('Failed to fetch article:', resultAction.error || resultAction.payload);
            }
        } catch (error) {
            console.error('Failed to load article:', error);
        }
    };

    // Creating something new: the kind is chosen in the list's "Novo" menu, so
    // the right editor opens straight away instead of a bare <select> appearing
    // above a form that may be the wrong one.
    const handleCreate = (typeId) => {
        dispatch(clearCurrentArticle());
        setCurrentDraft(null);
        setSavedSnapshot(null);
        setNewItemTypeId(typeId);
        setView(VIEW.EDITOR);
    };

    async function uploadHandler(file) {
        try {
            const resultAction = await dispatch(uploadMedia(file));

            if (uploadMedia.fulfilled.match(resultAction)) {
                return resultAction.payload;
            } else {
                const errorMessage = resultAction.payload || resultAction.error?.message || 'Failed to upload file';
                console.error('Failed to upload file:', errorMessage);
                // Throw the error so it can be caught by the editor
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error('Failed to upload file:', error);
            throw error; // Re-throw to be handled by the editor
        }
    }

    const editorProps = {
        initialContent: normalizedCurrentArticle,
        onSave: saveHandler,
        onUpload: uploadHandler,
        onSaveSuccess: closeEditor,
        loading: articleStatus === 'loading',
    };

    // #region RETURN
    if (view === VIEW.LIST) {
        return <ArticlesGrid onEditArticle={handleEditArticle} onCreate={handleCreate} />;
    }

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Editor header: the way back, plus what is being edited. Sticky so
                it survives a long body on a phone. */}
            <Box
                sx={{
                    position: 'sticky',
                    top: { xs: 56, md: 64 },
                    zIndex: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: { xs: 1, md: 2 },
                    py: 1,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Tooltip title='Voltar à lista (guarda rascunho)'>
                    <IconButton onClick={handleBackToList} aria-label='Voltar'>
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontWeight: 700 }}>
                        {isNewItem ? 'Novo conteúdo' : normalizedCurrentArticle.title || '(sem título)'}
                    </Typography>
                    {activeType && (
                        <Typography variant='caption' color='text.secondary' noWrap component='div'>
                            {activeType.display_name || activeType.name}
                        </Typography>
                    )}
                </Box>
                {dirty && <Chip size='small' color='warning' variant='outlined' label='Por guardar' />}
            </Box>

            {editorForm === 'etiqueta' ? (
                <EtiquetaEditor {...editorProps} articleTypeId={activeTypeId} />
            ) : (
                <ArticleEditor {...editorProps} draftInterval={20000} onContentChange={setCurrentDraft} newTypeId={newItemTypeId} />
            )}
        </Box>
    );
};

export default Articles;
